import React from "react";
import { ClassNameProps, makeClassName, Modal, ModalProps, Row, StyleProps, useCount } from "@hex-insights/core";
import { Icon } from "../../../../Components";
import { PostLink } from "../../../Links";
import { MediaAttachmentUtilities } from "../../../Utilities";
import { getPreloadMediaAttachments } from "../../MediaAttachment";
import { DetailDisplayPost, PostDetailDisplay, PostVisualAttachmentDetailDisplay } from "../DetailDisplay";
import { ControlledIndividualStatisticsTile } from "../Statistics";
import styles from "./styles.module.css";

export type AdminModalPost = DetailDisplayPost;

export type PostAdminModalProps = {
	post: AdminModalPost;
	initialIndex?: number;
} & Pick<ModalProps, "ifRef" | "onClose"> &
	Partial<ClassNameProps & StyleProps>;

export function PostAdminModal({ post, initialIndex, ...props }: PostAdminModalProps) {
	if (initialIndex !== undefined) {
		return <PostVisualAttachmentAdminModal post={post} initialIndex={initialIndex} {...props} />;
	}
	return <PostAdminOverviewModal post={post} {...props} />;
}

export function PostAdminOverviewModal({
	post,
	ifRef,
	onClose,
	className,
	style,
}: Omit<PostAdminModalProps, "initialIndex">) {
	const {
		isModalOpen: isVisualAttachmentModalOpen,
		openModal: openVisualAttachmentModal,
		closeModal: closeVisualAttachmentModal,
	} = Modal.useToggle();
	const [modalVisualAttachmentIndex, setModalVisualAttachmentIndex] = React.useState(0);
	const onClickVisualAttachment = React.useCallback(
		(index: number) => {
			setModalVisualAttachmentIndex(index);
			openVisualAttachmentModal();
		},
		[openVisualAttachmentModal],
	);

	return (
		<Modal
			ifRef={ifRef}
			size="large"
			onClose={onClose}
			className={makeClassName(styles["post-modal"], className)}
			style={style}
		>
			<Modal.Body>
				<PostDetailDisplay post={post} withAudience editable onClickVisualAttachment={onClickVisualAttachment} />

				<Modal.If condition={isVisualAttachmentModalOpen}>
					<PostVisualAttachmentAdminModal
						post={post}
						initialIndex={modalVisualAttachmentIndex}
						onClose={closeVisualAttachmentModal}
					/>
				</Modal.If>
			</Modal.Body>
			<Modal.Footer>
				<Row justify="space-between" align="center">
					<ControlledIndividualStatisticsTile postID={post.id} />

					<PostLink instance={post} className={makeClassName(styles["detail-link"], "link--no-text-decoration")}>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<span>View Page</span>
							<Icon.ArrowRight size={14} />
						</Row>
					</PostLink>
				</Row>
			</Modal.Footer>
		</Modal>
	);
}

export function PostVisualAttachmentAdminModal({
	post,
	initialIndex = 0,
	ifRef,
	onClose,
	className,
	style,
}: PostAdminModalProps) {
	const { visualAttachments } = React.useMemo(
		() => MediaAttachmentUtilities.splitMediaAttachments(post.mediaAttachments),
		[post.mediaAttachments],
	);

	const {
		count: index,
		decrement: decrementIndex,
		increment: incrementIndex,
	} = useCount(initialIndex, { mod: visualAttachments.length });
	const attachment = visualAttachments[index];

	const preloadAttachments = React.useMemo(
		() => getPreloadMediaAttachments(visualAttachments, index),
		[visualAttachments, index],
	);

	return (
		<Modal ifRef={ifRef} size="large" onClose={onClose} className={className} style={style}>
			<Modal.Body style={{ height: "100%" }}>
				<PostVisualAttachmentDetailDisplay
					post={post}
					index={index}
					mediaAttachment={attachment}
					preloadMediaAttachments={preloadAttachments}
					totalAttachments={visualAttachments.length}
					incrementIndex={incrementIndex}
					decrementIndex={decrementIndex}
					style={{ height: "100%" }}
				/>
			</Modal.Body>
		</Modal>
	);
}
